import React from "react";
import { ConfigProvider } from "@arco-design/web-react";
import enUS from "@arco-design/web-react/es/locale/en-US";
import "@arco-design/web-react/dist/css/arco.css";
import "./brand.css";
import LocalAuth from "./LocalAuth";
import RequireAuth from "./RequireAuth";
import AppBarAndDrawer from "./AppBarAndDrawer/AppBarAndDrawer";
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { SignIn, SignUp } from "./SignIn";
import { Home } from "./Home/Home";
import { useTheme } from "./theme";
import Campaigns from "./Campaigns/Campaigns";
import Settings from "./Settings/Settings";
import Account from "./Account/Account";
import EditCampaign from "./Campaigns/EditCampaign";
import Contacts from "./Contacts/Contacts";
import Segments from "./Segments/Segments";
import Templates from "./Templates/Templates";
import MailVariantEditor from "./Campaigns/MailVariantEditor";
import BroadcastMailVariantEditor from "./Broadcast/BroadcastEditor/MailVariants/BroadcastMailVariantEditor";
import ViewCampaign from "./Campaigns/ViewCampaign";
import FormVariantEditor from "./Campaigns/FormVariantEditor";
import Broadcasts from "./Broadcast/Broadcasts";
import EditBroadcast from "./Broadcast/BroadcastEditor/EditBroadcast";
import ViewBroadcast from "./Broadcast/BroadcastViewer/ViewBroadcast";
import LogoutButton from "./LogoutButton";
import Sequences from "./Sequences/Sequences";
import EditSequence from "./Sequences/EditSequence";
import SequenceStepEditor from "./Sequences/SequenceStepEditor";
import TemplateEditor from "./Templates/TemplateEditor";
import InvitePrompt from "./Invitations/InvitePrompt";
import ContactLists from "./ContactLists/ContactLists";
import FileAssets from "./FileAssets/FileAssets";
import Reports from "./Reports/Reports";
import Workflows from "./Workflows/Workflows";
import WorkflowDetail from "./Workflows/WorkflowDetail";
import Companies from "./Companies/Companies";
import Spy from "./Spy/Spy";
import WatchDetail from "./Spy/WatchDetail";
import SpyDomains from "./Admin/SpyDomains";
import BillingAccess from "./Admin/BillingAccess";
import DisabledAccountGate from "./Components/DisabledAccountGate";
import CompanyDetail from "./Companies/CompanyDetail";
import ContactDetail from "./Contacts/ContactDetail";
import useOrgFeatures from "./utils/useOrgFeatures";

// FeatureRoute shows its page only when the organization has the feature on;
// otherwise (say, an old bookmark) it goes home. Nothing shows while the
// settings load.
function FeatureRoute({ feature, children }) {
  const features = useOrgFeatures();
  if (!features.loaded) return null;
  return features[feature] ? children : <Navigate to="/" replace />;
}

// The Signups section used to live at /campaigns. Old links and bookmarks land
// on the same page under /signups.
function CampaignsRedirect() {
  const { pathname, search } = useLocation();
  return <Navigate to={pathname.replace(/^\/campaigns/, "/signups") + search} replace />;
}

// The authenticated app: nav shell + the protected routes. Rendered only when
// RequireAuth passes.
function AppShell({ currentTheme, setCurrentTheme }) {
  return (
    <div>
      <InvitePrompt />
      <div>
        <AppBarAndDrawer
          currentTheme={currentTheme}
          setCurrentTheme={setCurrentTheme}
        />
      </div>
      <div>
        <DisabledAccountGate>
        <Routes>
          <Route path="/home" element={<Home />} />

          {/* Signups (campaign) workflow */}
          <Route path={`/signups/:campaignId/edit/mail/:mailVariantId`} element={<MailVariantEditor />} />
          <Route path={`/signups/:campaignId/form/new`} element={<FormVariantEditor />} />
          <Route path={`/signups/:campaignId/edit/form/:formVariantId`} element={<FormVariantEditor />} />
          <Route path={`/signups/:campaignId/edit`} element={<EditCampaign />} />
          <Route path={`/signups/:campaignId/view`} element={<ViewCampaign />} />
          <Route exact path="/signups" element={<Campaigns />} />
          <Route path="/campaigns/*" element={<CampaignsRedirect />} />
          <Route exact path="/file-assets" element={<FileAssets />} />
          <Route exact path="/reports" element={<Reports />} />

          {/* Broadcast workflow */}
          <Route exact path="/broadcasts" element={<Broadcasts />} />
          <Route path={`/broadcasts/:broadcastId/edit`} element={<EditBroadcast />} />
          <Route path={`/broadcasts/:broadcastId/view`} element={<ViewBroadcast />} />
          <Route path={`/broadcasts/:broadcastId/email/:mailVariantId`} element={<BroadcastMailVariantEditor />} />

          {/* Contacts workflow */}
          <Route exact path="/contacts" element={<Contacts />} />
          <Route path="/contacts/:contactId" element={<ContactDetail />} />
          <Route exact path="/companies" element={<FeatureRoute feature="companies"><Companies /></FeatureRoute>} />
          <Route path="/companies/:companyId" element={<FeatureRoute feature="companies"><CompanyDetail /></FeatureRoute>} />
          <Route exact path="/segments" element={<Segments />} />
          <Route exact path="/templates" element={<Templates />} />
          <Route path="/templates/:templateId" element={<TemplateEditor />} />
          <Route exact path="/sequences" element={<Sequences />} />
          <Route path="/sequences/:sequenceId/edit" element={<EditSequence />} />
          <Route path="/sequences/:sequenceId/steps/:stepId/email" element={<SequenceStepEditor />} />
          <Route exact path="/workflows" element={<FeatureRoute feature="workflows"><Workflows /></FeatureRoute>} />
          <Route path="/workflows/:workflowId" element={<FeatureRoute feature="workflows"><WorkflowDetail /></FeatureRoute>} />
          <Route path="/spy" element={<Spy />} />
          <Route path="/spy/:watchId" element={<WatchDetail />} />
          <Route path="/admin/spy-domains" element={<SpyDomains />} />
          <Route path="/admin/accounts" element={<BillingAccess />} />
          <Route exact path="/lists" element={<ContactLists />} />

          <Route path="/account" element={<Account />} />
          <Route path="/settings" element={
            <Settings />
          } />
          <Route path="/" element={<Home />} />
        </Routes>
        </DisabledAccountGate>
      </div>
    </div>
  );
}

export default function App() {
  const [currentTheme, setCurrentTheme] = useTheme();
  return (
    <ConfigProvider locale={enUS}>
      <Router>
        <Routes>
          {/* Public routes — no shell, no auth guard. */}
          <Route path="/login" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/logout" element={<LogoutButton />} />
          {/* Dev-only LOCAL_AUTH pages; compiled out of production builds. */}
          {process.env.NODE_ENV === "development" && (
            <>
              <Route path="/register" element={<LocalAuth initialMode="register" />} />
              <Route path="/local-login" element={<LocalAuth initialMode="login" />} />
            </>
          )}

          {/* Everything else requires auth. */}
          <Route
            path="/*"
            element={
              <RequireAuth>
                <AppShell currentTheme={currentTheme} setCurrentTheme={setCurrentTheme} />
              </RequireAuth>
            }
          />
        </Routes>
      </Router>
    </ConfigProvider>
  );
}

// NOTE: This react materialui template originally came from here: https://react.school/material-ui/templates
